import dayjs from 'dayjs';
import qs from 'qs';
import { StrapiResponse } from 'src/types/http';
import {
  FidcFundHistory,
  GetFundsHistoryParams,
  Operation,
  UnderlyingDebtor,
} from 'src/types/operation';
import { FiagroOperation } from 'src/types/operations/fiagro';
import {
  CNPJ_DIGITS_LENGTH,
  formatCNPJ,
  normalizeCNPJ,
} from 'src/utils/document';

import { api, publicApi } from '../../api/api';

export const getSecondaryFilters = async (hash: string) => {
  const { data } = await api.get(`/getSecondaryFilters?operationId=${hash}`);
  return data;
};

export const filterSecondary = async (values: any) => {
  const { data } = await api.post('/filterSecondary', values);
  return data;
};

export const exportSecondary = async (values: any) => {
  const { data } = await api.post('/filterSecondary/export', values);
  return data;
};

export const countOperations = async ({ market }: { market: string }) => {
  let request;
  const query = qs.stringify(
    {
      market,
    },
    {
      encodeValuesOnly: true,
    },
  );

  if (market.toUpperCase() === 'CR') {
    request = publicApi.get('/ipanema-two/basic-view-operations/count');
  } else {
    request = publicApi.get(`/operations-detailed/count?${query}`);
  }
  const { data } = await request;
  return data;
};

export const findOperationsByName = async (name: string) => {
  const query = qs.stringify(
    {
      sort: ['name:asc'],
      filters: {
        name: {
          $containsi: name,
        },
      },
      populate: ['market'],
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get(`/operacoes?${query}`);
  return data;
};

export const findOperationsSearch = async (
  search: string,
  page = 1,
  pageSize = 20,
) => {
  const normalized = normalizeCNPJ(search);
  const isCNPJ =
    normalized.length === CNPJ_DIGITS_LENGTH ? formatCNPJ(normalized) : null;

  let filters;

  if (!search.trim()) {
    filters = {
      market: {
        name: 'FIDC',
      },
    };
  } else {
    filters = {
      name: search,
      ...(isCNPJ && { cnpj: isCNPJ }),
      market: {
        name: 'FIDC',
      },
    };
  }

  const query = qs.stringify(
    {
      filters,
      pagination: {
        page,
        pageSize,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  const response = await api.get(`/wallet/find-by-name-and-document?${query}`);
  const { data, meta } = response.data;
  return {
    data: data,
    nextPage:
      meta?.pagination?.page < meta?.pagination?.pageCount
        ? meta?.pagination?.page + 1
        : null,
    total: meta?.pagination?.total,
  };
};

export type FindOperationsByCNPJsResponse = {
  found: Operation[];
  notFound: string[];
  nonConforming: string[];
};

export const findOperationsByCNPJs = async (
  cnpjs?: string[],
  markets: string[] = ['FIDC'],
): Promise<FindOperationsByCNPJsResponse> => {
  if (!cnpjs?.length) return { found: [], notFound: [], nonConforming: [] };

  const documents = cnpjs.map((cnpj) => {
    const normalized = normalizeCNPJ(cnpj);
    return normalized.length === CNPJ_DIGITS_LENGTH
      ? formatCNPJ(normalized)
      : cnpj;
  });

  const { data } = await api.post('/wallet/get-operations-by-cnpj', {
    data: {
      markets,
      documents,
    },
  });

  return data.data;
};

export const getOperationRelatedArticles = async ({
  start,
  limit,
  operationId,
}: {
  start: number;
  limit: number;
  operationId: string;
}) => {
  const query = qs.stringify(
    {
      pagination: {
        start,
        limit,
      },
      filters: {
        relatedOperations: {
          hashId: operationId,
        },
        publishedAt: {
          $null: false,
        },
      },
      populate: ['headerImage', 'markets'],
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await publicApi.get(`/artigos?${query}`);
  return data;
};

export const getOperationForHome = async () => {
  const query = qs.stringify(
    {
      fields: ['name', 'hashId', 'slug'],
      populate: ['market'],
      pagination: {
        page: 1,
        pageSize: 6,
      },
      sort: ['createdAt:desc'],
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get(`/operacoes?${query}`);
  return data;
};

export const getColumnsBasicView = async ({ market }: { market: string }) => {
  if (market === 'cr') {
    market = 'cra';
  }
  const query = qs.stringify(
    {
      market,
    },
    {
      encodeValuesOnly: true,
    },
  );

  return api.get(`/operations-detailed/columns?${query}`);
};

export const getColumns = async ({
  market,
  indicator,
}: {
  market: string;
  indicator: string;
}) => {
  if (indicator === 'ofertas') {
    const { data } = await api.get('/meta-ofertas-e-negocios/columns');
    return data;
  }

  const query = qs.stringify(
    {
      market,
      indicator,
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get(`/meta/columns?${query}`);
  return data;
};

export const getOffersForHome = async () => {
  const { data } = await api.get('/meta-ofertas-e-negocios/latest');
  return data;
};

export const getLatestSecondaryMarketNegotiations = async () => {
  const { data } = await api.get('/home/secondary-market/latest');
  return data;
};

export const getOffers = async ({
  market,
  page = 1,
  pageSize = 1000,
  filters,
  sorting,
}: {
  market: string;
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
  sorting?: Record<string, any>;
}) => {
  const query = qs.stringify(
    {
      market,
      page,
      pageSize,
      ...(filters ? { filters } : {}),
      ...(sorting ? { sorting } : {}),
    },
    { encodeValuesOnly: true },
  );
  const { data } = await api.get(`/meta-ofertas-e-negocios/all?${query}`);

  return {
    data: data.data,
    totalCount: data.meta.pagination.total,
    currentPage: data.meta.pagination.page,
    totalPages: data.meta.pagination.pageCount,
  };
};

export const getAll = async (
  {
    market,
    indicator,
    cnpj,
    permission,
    mode,
    page = 1,
    pageSize = 1000,
    filters,
    sorting,
    populate,
  }: {
    market?: string;
    indicator?: string;
    cnpj?: string;
    permission?: string;
    mode: 'meta' | 'operation';
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
    sorting?: Record<string, any>;
    populate?: Record<string, any>;
  },
  setProgress?: (value: number) => void,
) => {
  if (indicator === 'ofertas') {
    return getOffers({
      market: market ?? '',
      page,
      pageSize,
      filters,
      sorting,
    });
  }

  let request;
  const query = qs.stringify(
    {
      market: mode === 'meta' ? market : undefined,
      indicator,
      permission,
      page,
      pageSize,
      ...(cnpj
        ? {
            parameters: {
              filters: {
                cnpjFundoCota: cnpj,
              },
            },
          }
        : undefined),
      ...(mode === 'operation'
        ? {
            parameters: {
              filters: {
                market,
              },
            },
          }
        : undefined),
      ...(filters ? { filters } : {}),
      ...(sorting ? { sorting } : {}),
      ...(populate ? { populate } : {}),
    },
    {
      encodeValuesOnly: true,
    },
  );
  const config = {
    onDownloadProgress: (progressEvent: any) => {
      if (setProgress) {
        const percentage = Math.round(
          (progressEvent.loaded * 100) /
            (progressEvent.total ??
              progressEvent.srcElement.getResponseHeader('content-length')),
        );
        setProgress(percentage);
      }
    },
  };

  if (market === 'cr') {
    request = api.get(
      `/ipanema-two/operation/basic-view-operations?${query}`,
      config,
    );
  } else {
    request = api.get(`/meta/all?${query}`, config);
  }

  const { data } = await request;

  return {
    data: data.data,
    totalCount: data.meta.pagination.total,
    currentPage: data.meta.pagination.page,
    totalPages: data.meta.pagination.pageCount,
  };
};

export const getFIIWalletCRICRA = async ({ cnpj }: { cnpj: string }) => {
  const query = qs.stringify(
    {
      filters: {
        cnpjFundo: cnpj,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const {
    data: { data },
  } = await api.get(`/meta-fii-carteira-cri-cras?${query}`);
  return data;
};

export const getFIIWalletFII = async ({ cnpj }: { cnpj: string }) => {
  const query = qs.stringify(
    {
      filters: {
        cnpjFundo: cnpj,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const {
    data: { data },
  } = await api.get(`/meta-fii-carteira-fiis?${query}`);
  return data;
};

export const getFIIWalletFIDC = async ({ cnpj }: { cnpj: string }) => {
  const query = qs.stringify(
    {
      filters: {
        cnpjFundo: cnpj,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const {
    data: { data },
  } = await api.get(`/meta-fii-carteira-fidcs?${query}`);
  return data;
};

export const getFIIWalletProperty = async (params: any) => {
  const query = qs.stringify(
    {
      market: params.mode === 'meta' ? params.market : undefined,
      indicator: params.indicator,
      permission: params.permission,
      parameters: {
        filters: {
          cnpjFundo: params.cnpj,
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(`/meta-fii-ativo-imovels?${query}`);
  return data;
};

export const getCriCraInvestorsPositions = async ({
  operationId,
  permission,
  page = 1,
  pageSize = 1000,
  filters = {},
  sorting = {},
}: {
  operationId: string;
  permission: string;
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
  sorting?: Record<string, any>;
}) => {
  const query = qs.stringify(
    {
      permission,
      filters: {
        relatedOperation: {
          operationId,
        },
        ...filters,
      },
      ...sorting,
      populate: {
        investor: {
          fields: ['name', 'slug', 'operationId'],
        },
        relatedOperation: {
          fields: ['class'],
        },
      },
      page,
      pageSize,
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get<{
    data: any[];
    meta: {
      pagination: {
        page: number;
        pageCount: number;
        pageSize: number;
        total: number;
      };
    };
  }>(`/cri-cra-investors-positions?${query}`);
  return data;
};

export const getCRIReceivablesDue = async ({
  operationId,
}: {
  operationId: string;
}) => {
  const query = qs.stringify(
    {
      filters: {
        operation: {
          operationId,
        },
      },
      sort: ['dataReferencia:desc'],
    },
    {
      encodeValuesOnly: true,
    },
  );

  const {
    data: { data },
  } = await api.get(`/meta-cri-recebiveis-a-vencers?${query}`);
  return data;
};

export const getCRAReceivablesDue = async ({
  operationId,
}: {
  operationId: string;
}) => {
  const query = qs.stringify(
    {
      filters: {
        operation: {
          operationId,
        },
      },
      sort: ['dataReferencia:desc'],
    },
    {
      encodeValuesOnly: true,
    },
  );

  const {
    data: { data },
  } = await api.get(`/meta-cra-recebiveis-a-vencers?${query}`);
  return data;
};

export const getOperationsFilters = async ({
  market,
  type,
  indicator,
}: {
  market: string;
  type: string;
  indicator?: string;
}) => {
  const query = qs.stringify(
    {
      filters: {
        market: {
          name: market,
        },
        type,
        indicator,
      },
      sort: ['createdAt:desc'],
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(`/operations-filters-by-user?${query}`);
  return data;
};

export const createOperationFilter = async (values: any) => {
  const { data } = await api.post('/operations-filters', values);
  return data;
};

export const deleteOperationsFilter = async (id: number) => {
  const {
    data: { data },
  } = await api.delete(`/operations-filters/${id}`);
  return data;
};

export type FundSearchOption = {
  cnpj: string;
  name: string;
  label: string;
  value: string;
};

export type ClassSerieSearchOption = {
  classSerie: string;
  label: string;
  value: string;
};

type FundOptionResponse = {
  cnpjFundo?: string;
  denominacaoSocial?: string;
};

type ClassSerieOptionResponse = {
  classeSerie?: string;
  classe_serie?: string;
  classSerie?: string;
  label?: string;
  value?: string;
};

type FundSearchParams = {
  search?: string;
  page?: number;
  pageSize?: number;
};

type ClassSerieSearchParams = {
  cnpj: string;
  search?: string;
};

export const sanitizeClassSerie = (raw: string) =>
  raw
    .replace(/\?nica/g, 'Única')
    .replace(/\?nico/g, 'Único')
    .replace(/S\?rie/g, 'Série');

const toFundSearchOption = (
  option: FundOptionResponse,
): FundSearchOption | null => {
  const cnpj = option.cnpjFundo ?? '';
  const name = option.denominacaoSocial ?? cnpj;

  if (!cnpj) return null;

  return {
    cnpj,
    name,
    label: name,
    value: cnpj,
  };
};

const toClassSerieSearchOption = (
  option: ClassSerieOptionResponse,
): ClassSerieSearchOption | null => {
  const classSerie =
    option.classSerie ??
    option.classeSerie ??
    option.classe_serie ??
    option.value ??
    option.label ??
    '';

  if (!classSerie) return null;

  const label = sanitizeClassSerie(option.label ?? classSerie);

  return {
    classSerie,
    label,
    value: classSerie,
  };
};

export const getFundClassAndSeries = async ({
  cnpj,
  search = '',
}: ClassSerieSearchParams): Promise<
  StrapiResponse<ClassSerieSearchOption[]>
> => {
  const query = qs.stringify(
    {
      cnpj,
      search,
      fields: ['classeSerie', 'classe_serie'],
      sort: ['classeSerie:asc'],
      filters: {
        cnpjFundo: {
          $eq: cnpj,
        },
        ...(search && {
          classeSerie: {
            $containsi: search,
          },
        }),
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get<StrapiResponse<ClassSerieOptionResponse[]>>(
    `/meta-rentabilidade-mensal-de-cotas-de-fidcs?${query}`,
  );

  return {
    ...data,
    data: data.data.flatMap((option) => {
      const mapped = toClassSerieSearchOption(option);
      return mapped ? [mapped] : [];
    }),
  };
};

export const getFundByName = async ({
  search = '',
  page = 1,
  pageSize = 50,
}: FundSearchParams = {}): Promise<StrapiResponse<FundSearchOption[]>> => {
  const query = qs.stringify(
    {
      search,
      page,
      pageSize,
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get<StrapiResponse<FundOptionResponse[]>>(
    `/meta-rentabilidade-mensal-de-cotas-de-fidc/fund-options?${query}`,
  );

  return {
    ...data,
    data: data.data.flatMap((option) => {
      const mapped = toFundSearchOption(option);
      return mapped ? [mapped] : [];
    }),
  };
};

export const getFundsHistory = async ({
  CNPJs,
  period,
  classSerie,
}: GetFundsHistoryParams): Promise<FidcFundHistory[]> => {
  if (!CNPJs.length) return [];

  const now = dayjs();

  const graterThanDate = period
    ? now.subtract(period, 'months').format('YYYY-MM-DD')
    : null;

  const query = qs.stringify(
    {
      fields: [
        'cnpjFundo',
        'denominacaoSocial',
        'rentabilidadeMensalPct',
        'dataCompetencia',
        'classeSerie',
      ],
      filters: {
        cnpjFundo: {
          $in: CNPJs,
        },
        ...(graterThanDate && {
          dataCompetencia: {
            $gte: graterThanDate,
          },
        }),
        ...(classSerie && {
          classeSerie: {
            $eq: classSerie,
          },
        }),
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(
    `/meta-rentabilidade-mensal-de-cotas-de-fidcs?${query}`,
  );

  return data.data;
};

export const getUnderlyingDebtors = async ({
  operationId,
  market,
  page,
  pageSize,
}: {
  operationId: string;
  market: string;
  page: number;
  pageSize: number;
}) => {
  const query = qs.stringify(
    {
      filters: {
        operationId,
      },
      pagination: {
        page,
        pageSize,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  if (market === 'cr') {
    const { data } = await api.get<StrapiResponse<UnderlyingDebtor[]>>(
      `/ipanema-two/operation/get-underlying-debtors?${query}`,
    );

    return data;
  }

  const { data } = await api.get<StrapiResponse<UnderlyingDebtor[]>>(
    `/meta-${market}-cedentes-e-devedoress?${query}`,
  );
  return data;
};

export const getFiagroOperationsList = async ({
  page = 1,
  pageSize = 10,
  filters,
}: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
}) => {
  const query = qs.stringify(
    {
      pagination: {
        page,
        pageSize,
      },
      ...(filters ? { filters } : {}),
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get<StrapiResponse<FiagroOperation[]>>(
    `/meta-fiagro-operationss?${query}`,
  );
  return data;
};

export const generateCsv = async (params: {
  market?: string;
  mode?: string;
  indicator?: string;
  permission?: string;
  filters?: Record<string, any>;
  sorting?: Record<string, any>;
}) => {
  const query = qs.stringify(
    {
      ...(params.mode === 'operation'
        ? {
            parameters: {
              filters: {
                market: params.market,
              },
            },
          }
        : undefined),
      ...params,
    },
    { encodeValuesOnly: true },
  );
  const { data } = await api.get(`/meta/generate-csv?${query}`);
  return data;
};

export const generateOffersCsv = async (params: {
  market?: string;
  filters?: Record<string, any>;
  sorting?: Record<string, any>;
}) => {
  const query = qs.stringify(params, { encodeValuesOnly: true });
  const { data } = await api.get(
    `/meta-ofertas-e-negocios/generate-csv?${query}`,
  );
  return data;
};

export const getCsvStatus = async (jobId: string) => {
  const { data } = await api.get(`/meta/csv-status/${jobId}`);
  return data;
};

export const getOffersCsvStatus = async (jobId: string) => {
  const { data } = await api.get(
    `/meta-ofertas-e-negocios/csv-status/${jobId}`,
  );
  return data;
};

export const downloadCsv = async (jobId: string) => {
  const response = await api.get(`/meta/download-csv/${jobId}`, {
    responseType: 'blob',
  });
  return response;
};

export const downloadOffersCsv = async (jobId: string) => {
  const response = await api.get(
    `/meta-ofertas-e-negocios/download-csv/${jobId}`,
    { responseType: 'blob' },
  );
  return response;
};
